/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { isDateBeforeOrEqual } from 'src/helpers/utils';
import type {
  AdminSupportChatMessageListResponse,
  ApplicantListSub,
  ChatForApplicantSub,
  ChatMessageBody,
  ChatMessageListResponse,
  ChatMessageResponse,
  GetChatGroupUsersResponse,
  GetOrganizationUsersListResponse,
  LatestChatList,
  LatestChatListResponse,
  SupportChat,
  SupportChatCount,
  SupportChatListResponse,
} from 'src/openapi';
import {
  getApplicantStatusInfo,
  jobByIdOrAliasForCandidate,
} from '../actions/candidateLayout';
import {
  fetchChatApplicants,
  fetchOrgApplicantChats,
  fetchOrgChatJobs,
  fetchApplicantChatJobs,
  fetchChats,
  sendMessage,
  fetchEmployerChatTemplateList,
  deleteEmployerChatTemplate,
  createEmployerChatTemplate,
  fetchPostedJobDetails,
  updateEmployerChatTemplate,
  addChatReaction,
  updateChatReaction,
  deleteChatReaction,
  updateJobChat,
  deleteJobChat,
  rejectJobInvite,
  getSupportMessages,
  getChatMessages,
  sendChatMessage,
  getAdminSupportMessages,
  getAdminChatMessages,
  resolveSupportChat,
  sendAdminChatMessage,
  getAdminChatStats,
  getFrontChatList,
  fetchGroupChatItems,
  sendGroupChat,
  initiateDirectChat,
  exitGroup,
  chatBlockUnblock,
  fetchEmployerPostedJobs,
  getOrgJobsUnreadMessageCount,
  updateSupportOrTeamChat,
  deleteSupportTeamChat,
  fetchTeamSupportUnreadMessageCount,
  markJobChatSeen,
  markSupportTeamChatSeen,
  initiateSupportChat,
} from '../actions/chat';
import type {
  ChatInitialState,
  Filters,
  SocketChatReactionResponse,
  SelectedJobIdOrgId,
  SocketChatSeenResponse,
  AdminChatState,
  FloatingChat,
  ChatToggleView,
  FloatingChatUnreadCountDecrementPayload,
  NewJobChatMessageRecievedSocketPayload,
  AdminChatFilters,
} from '../types/chat';

// Skip the unshift if the same message id is already in the list — guards
// against the socket-echo / HTTP-response race that duplicates messages.
const unshiftMessageIfNew = <T extends { id?: string | null }>(
  list: T[] | undefined | null,
  item: T,
): boolean => {
  if (!list) return false;
  if (item.id != null && list.some((x) => x.id === item.id)) return false;
  list.unshift(item);
  return true;
};

// The generated ChatMessageResponse types `support_chat_id` as nullable, but
// SupportChat / LatestChatList type it as string | undefined. Normalize null →
// undefined so a message payload can seed a conversation-list entry.
const asChatListEntry = <T extends { support_chat_id?: string | null }>(
  payload: T,
): Omit<T, 'support_chat_id'> & { support_chat_id?: string } => ({
  ...payload,
  support_chat_id: payload.support_chat_id ?? undefined,
});

// Finds the conversation matching `predicate`, merges `getPatch(item)` into
// it, and moves it to index 0 — a new message always makes a conversation
// the most recent, so it belongs at the top of the sidebar list.
const patchAndBumpToFront = <T>(
  list: T[],
  predicate: (item: T) => boolean,
  getPatch: (item: T) => Partial<T>,
): boolean => {
  const idx = list.findIndex(predicate);
  if (idx === -1) return false;
  const [item] = list.splice(idx, 1);
  list.unshift({ ...item, ...getPatch(item) });
  return true;
};

const adminChatInitialState: AdminChatState = {
  filters: {
    query: '',
    type: 'All',
  },
  supportChats: null,
  selectedChat: null,
  selectedChatItems: null,
  chatStats: {
    support_chats_count: 0,
    resolved_count: 0,
    unresolved_count: 0,
  },
  isLoading: false,
  isResolving: false,
  isSupportMessagesLoading: false,
};

const floatingChatBox: FloatingChat = {
  isOpen: true,
  isDrawerOpen: false,
  chatToggleView: 'Team messages',
  screen: 'home',
  supportChats: {
    support_chat_details: [],
    page_no: 1,
    page_size: 10,
    total_pages: 1,
  },
  isLoading: false,
  selectedChat: null,
  selectedChatItems: null,
  teamChats: {
    latest_chat_details: [],
    page_no: 1,
    page_size: 10,
    total_pages: 1,
  },
  selectedTeamChatMembers: [],
  selectedTeamChatItems: null,
  searchQuery: '',
  unreadCounts: null,
  selectedTeamChat: null,
  isChatBubbleVisible: false,
};

const initialState: ChatInitialState = {
  applicantChatJobs: null,
  employerChatApplicants: null,
  employerSelectedJobIdOrgId: null,
  employerSelectedJobDetails: null,
  employerChatTemplates: null,
  filters: {
    query: '',
    status: [],
    isChatStarted: true,
  },
  applicantSelectedJob: null,
  employerSelectedApplicant: null,
  chats: null,
  isFetchingApplicantChatJobs: false,
  isFetchingEmployerChatApplicants: false,
  applicantJobDetails: null,
  adminChat: adminChatInitialState,
  floatingChatBox,
  employerPostedJobs: null,
  employerOrgJobsUnreadMessage: null,
  applicantJobsUnreadMessageCount: 0,
  employerApplicantListRefreshKey: 0,
  candidateChatJobsRefreshKey: 0,
  orgApplicantChats: null,
  isFetchingOrgApplicantChats: false,
  isFetchingOrgApplicantChatsMore: false,
  orgChatJobs: null,
  isFetchingOrgChatJobs: false,
};

const chatSlice = createSlice({
  initialState,
  name: 'chat',
  reducers: {
    setFilters: (state, action: PayloadAction<Filters>) => {
      state.filters = action.payload;
    },
    setApplicantSelectedJob: (
      state,
      action: PayloadAction<ChatForApplicantSub | null>,
    ) => {
      state.applicantSelectedJob = action.payload;
      state.chats = null;

      if (action.payload?.unread_message_count) {
        if (state.applicantChatJobs?.company_details) {
          state.applicantChatJobs.company_details =
            state.applicantChatJobs.company_details.map((job) => {
              if (job.job_id === action.payload?.job_id) {
                return { ...job, unread_message_count: 0 };
              }
              return job;
            });
        }
        state.applicantJobsUnreadMessageCount = Math.max(
          0,
          (state.applicantJobsUnreadMessageCount ?? 0) -
            (action.payload.unread_message_count ?? 0),
        );
      }
    },
    setEmployerSelectedApplicant: (
      state,
      action: PayloadAction<ApplicantListSub | null>,
    ) => {
      state.employerSelectedApplicant = action.payload;
      state.chats = null;

      if (
        action.payload?.unread_message_count &&
        state.employerChatApplicants?.all_applicants
      ) {
        state.employerChatApplicants.all_applicants =
          state.employerChatApplicants.all_applicants.map((applicant) => {
            if (applicant.user?.id === action.payload?.user?.id) {
              return { ...applicant, unread_message_count: 0 };
            }
            return applicant;
          });
      }

      if (
        action.payload?.unread_message_count &&
        state.employerPostedJobs?.jobs
      ) {
        state.employerPostedJobs.jobs = state.employerPostedJobs.jobs.map(
          (job) => {
            if (job.id === action.payload?.job_id) {
              return {
                ...job,
                total_unseen_chats: Math.max(
                  0,
                  (job.total_unseen_chats ?? 0) -
                    (action.payload?.unread_message_count ?? 0),
                ),
              };
            }
            return job;
          },
        );
      }

      // The org-wide landing inbox tracks unread via its own `unread_count`,
      // which none of the blocks above touch — so without this the opened row
      // keeps its badge until a full inbox refetch. Not gated on the payload's
      // unread_message_count (which comes from the job-scoped list and can be 0
      // or absent even when the org row shows unread). Matched on user id +
      // job_alias so the same applicant's rows for other roles stay untouched.
      if (action.payload?.user?.id && state.orgApplicantChats?.applicants) {
        state.orgApplicantChats.applicants =
          state.orgApplicantChats.applicants.map((applicant) =>
            applicant.id === action.payload?.user?.id &&
            applicant.job_alias === action.payload?.job_alias
              ? { ...applicant, unread_count: 0 }
              : applicant,
          );
      }
    },
    // `updateHiringStatus` (redux/actions/applicantReview.ts) writes the new
    // status into the applicantReview slice's own applicant list, not here —
    // this mirrors that update into the chat slice's copy of the applicant
    // so the open conversation's panel reflects it immediately.
    setEmployerSelectedApplicantHiringStatus: (
      state,
      action: PayloadAction<string>,
    ) => {
      if (state.employerSelectedApplicant) {
        state.employerSelectedApplicant.hiring_status = action.payload;
      }
      if (state.employerChatApplicants?.all_applicants) {
        state.employerChatApplicants.all_applicants =
          state.employerChatApplicants.all_applicants.map((applicant) =>
            applicant.id === state.employerSelectedApplicant?.id
              ? { ...applicant, hiring_status: action.payload }
              : applicant,
          );
      }
      // Mirror into the org-wide inbox list too, so a status change (e.g. the
      // rejected-row tint) reflects immediately on the landing surface without
      // a refetch. Its rows key on the applicant's user id (+ job_alias), not
      // the JobApplicants row id used above.
      if (state.orgApplicantChats?.applicants) {
        const selectedUserId = state.employerSelectedApplicant?.user?.id;
        const selectedJobAlias = state.employerSelectedApplicant?.job_alias;
        state.orgApplicantChats.applicants =
          state.orgApplicantChats.applicants.map((applicant) =>
            applicant.id === selectedUserId &&
            applicant.job_alias === selectedJobAlias
              ? { ...applicant, hiring_status: action.payload }
              : applicant,
          );
      }
    },
    setEmployerSelectedJobIdOrgId: (
      state,
      action: PayloadAction<SelectedJobIdOrgId>,
    ) => {
      state.employerSelectedJobIdOrgId = action.payload;
      state.employerChatApplicants = null;
      state.employerSelectedApplicant = null;
      state.employerSelectedJobDetails = null;
      state.chats = null;
    },
    addChatReactionSocket: (
      state,
      action: PayloadAction<SocketChatReactionResponse>,
    ) => {
      if (state.chats?.all_chats) {
        state.chats.all_chats = state.chats.all_chats.map((chat) => {
          if (chat.id === action.payload.chat_id) {
            const updatedChatData = { ...chat };
            updatedChatData.reactions = updatedChatData.reactions?.concat({
              chat_id: action.payload.chat_id,
              created_at: action.payload.created_at,
              from_user_id: action.payload.from_user_id,
              id: action.payload.id,
              message: action.payload.message,
            });
            return updatedChatData;
          }
          return chat;
        });
      }
    },
    updateChatReactionSocket: (
      state,
      action: PayloadAction<SocketChatReactionResponse>,
    ) => {
      if (state.chats?.all_chats) {
        state.chats.all_chats = state.chats.all_chats.map((chat) => {
          if (chat.id === action.payload.chat_id) {
            const updatedChat = { ...chat };
            updatedChat.reactions = updatedChat.reactions?.map((reaction) => {
              if (reaction.id === action.payload.id) {
                return { ...reaction, message: action.payload.message };
              }
              return reaction;
            });
            return updatedChat;
          }
          return chat;
        });
      }
    },
    decrementOrgJobsUnreadMessage: (
      state,
      action: PayloadAction<{ jobId: string; count: number }>,
    ) => {
      if (!state.employerOrgJobsUnreadMessage) {
        return;
      }
      // update total count
      state.employerOrgJobsUnreadMessage.total_unread_job_message = Math.max(
        0,
        (state.employerOrgJobsUnreadMessage.total_unread_job_message ?? 0) -
          action.payload.count,
      );

      // update list data
      if (state.employerOrgJobsUnreadMessage.unread_msg_job_list) {
        state.employerOrgJobsUnreadMessage.unread_msg_job_list =
          state.employerOrgJobsUnreadMessage.unread_msg_job_list.map((job) => {
            if (job.job_id === action.payload.jobId) {
              return {
                ...job,
                unread_count: Math.max(
                  0,
                  (job.unread_count ?? 0) - action.payload.count,
                ),
              };
            }

            return job;
          });
      }
    },
    deleteChatReactionSocket: (
      state,
      action: PayloadAction<SocketChatReactionResponse>,
    ) => {
      if (state.chats?.all_chats) {
        state.chats.all_chats = state.chats.all_chats.map((chat) => {
          if (chat.id === action.payload.chat_id) {
            const updatedChat = { ...chat };
            updatedChat.reactions = updatedChat.reactions?.filter(
              (reaction) => reaction.id !== action.payload.id,
            );
            return updatedChat;
          }
          return chat;
        });
      }
    },
    chatSeenSocket: (state, action: PayloadAction<SocketChatSeenResponse>) => {
      if (
        (state.employerSelectedApplicant?.job_id === action.payload.job_id ||
          state.applicantSelectedJob?.job_id === action.payload.job_id) &&
        state.chats?.all_chats
      ) {
        state.chats.all_chats = state.chats.all_chats.map((chat) => {
          if (
            !chat.is_chat_seen &&
            isDateBeforeOrEqual(chat.created_at, action.payload.created_at)
          ) {
            return { ...chat, is_chat_seen: true };
          }
          return chat;
        });
      }
    },
    newJobChatMessageRecievedSocket: (
      state,
      action: PayloadAction<NewJobChatMessageRecievedSocketPayload>,
    ) => {
      const { newChat, isChatPage, type } = action.payload;
      // if employer recieved new message and current page is chat page
      const isEmployerSelectedApplicant =
        state.employerSelectedApplicant?.user?.id ===
          action.payload.newChat.applicant_id &&
        state.employerSelectedApplicant?.job_id ===
          action.payload.newChat.job_id;
      if (type === 'employer' && isChatPage) {
        // if there is new message for selected applicant
        if (isEmployerSelectedApplicant) {
          // update chats
          const inserted = unshiftMessageIfNew(state.chats?.all_chats, {
            ...newChat,
            attachment_url: newChat.attachment_url ?? undefined,
          });

          const fromUser = state.chats?.all_users?.find(
            (user) => user.user_id === newChat.from_user_id,
          );

          if (!fromUser) {
            state.chats?.all_users?.push({
              first_name: newChat.first_name,
              last_name: newChat.last_name,
              picture: newChat.picture,
              user_id: newChat.from_user_id,
            });
          }

          if (state.chats && inserted) {
            state.chats.total_chat_count =
              (state.chats.total_chat_count ?? 0) + 1;
          }

          // update last message and bump the conversation to the top
          if (state.employerChatApplicants?.all_applicants) {
            patchAndBumpToFront(
              state.employerChatApplicants.all_applicants,
              (applicant) => applicant.user?.id === newChat.applicant_id,
              () => ({
                latest_message: newChat.message,
                latest_message_time: newChat.created_at,
              }),
            );
          }
        }
        // if selected applicant is not new message applicant but applicant
        // is related to job
        else if (
          state.employerSelectedJobDetails?.job?.id === newChat.job_id &&
          state.employerChatApplicants?.all_applicants
        ) {
          // update last message and unread count, and bump to the top
          const isFound = patchAndBumpToFront(
            state.employerChatApplicants.all_applicants,
            (applicant) => applicant.user?.id === newChat.applicant_id,
            (applicant) => ({
              latest_message: newChat.message,
              latest_message_time: newChat.created_at,
              unread_message_count: (applicant.unread_message_count ?? 0) + 1,
            }),
          );

          // Applicant isn't in the currently loaded list (e.g. first message
          // from a newly-applied candidate) — the socket payload doesn't carry
          // enough applicant data to synthesize a list row, so signal the page
          // to refetch the full list instead of silently dropping the message.
          if (!isFound) {
            state.employerApplicantListRefreshKey += 1;
          }
        }
      }

      // update jobs unread count if applicant in not selected
      // NOTE: block will execute even if employer is not on chat page
      if (
        type === 'employer' &&
        (!state.employerSelectedApplicant?.user ||
          !isEmployerSelectedApplicant ||
          !isChatPage)
      ) {
        // update unread count in dropdown jobs
        if (state.employerPostedJobs?.jobs) {
          state.employerPostedJobs.jobs = state.employerPostedJobs.jobs.map(
            (job) => {
              if (job.id === newChat.job_id) {
                return {
                  ...job,
                  total_unseen_chats: (job.total_unseen_chats ?? 0) + 1,
                };
              }
              return job;
            },
          );
        }

        // update unread count for notification on leftside bar
        if (state.employerOrgJobsUnreadMessage) {
          state.employerOrgJobsUnreadMessage.total_unread_job_message =
            (state.employerOrgJobsUnreadMessage.total_unread_job_message ?? 0) +
            1;
          state.employerOrgJobsUnreadMessage.unread_msg_job_list =
            state.employerOrgJobsUnreadMessage.unread_msg_job_list?.map(
              (job) => {
                if (job.job_id === action.payload.newChat.job_id) {
                  return { ...job, unread_count: (job.unread_count ?? 0) + 1 };
                }
                return job;
              },
            );
        }
      }

      // if chat is applicant and user is on chat page
      if (type === 'candidate' && isChatPage) {
        // if job is selected
        if (
          state.applicantSelectedJob?.job_id === action.payload.newChat.job_id
        ) {
          // update chats
          const inserted = unshiftMessageIfNew(state.chats?.all_chats, {
            ...newChat,
            attachment_url: newChat.attachment_url ?? undefined,
          });

          const fromUser = state.chats?.all_users?.find(
            (user) => user.user_id === newChat.from_user_id,
          );

          if (!fromUser) {
            state.chats?.all_users?.push({
              first_name: newChat.first_name,
              last_name: newChat.last_name,
              picture: newChat.picture,
              user_id: newChat.from_user_id,
            });
          }

          if (state.chats && inserted) {
            state.chats.total_chat_count =
              (state.chats.total_chat_count ?? 0) + 1;
          }
          // update last message and bump the conversation to the top
          if (state.applicantChatJobs?.company_details) {
            patchAndBumpToFront(
              state.applicantChatJobs.company_details,
              (job) => job.job_id === newChat.job_id,
              () => ({
                latest_message: newChat.message,
                latest_message_time: newChat.created_at,
              }),
            );
          }
        }
        // if selected job is not new message job
        else if (state.applicantChatJobs?.company_details) {
          // update last message and unread count, and bump to the top
          const isFound = patchAndBumpToFront(
            state.applicantChatJobs.company_details,
            (job) => job.job_id === newChat.job_id,
            (job) => ({
              latest_message: newChat.message,
              latest_message_time: newChat.created_at,
              unread_message_count: (job.unread_message_count ?? 0) + 1,
            }),
          );

          // Job isn't in the currently loaded list (e.g. first message from a
          // company the candidate hasn't chatted with before) — same
          // insufficient-payload issue as the employer branch above.
          if (!isFound) {
            state.candidateChatJobsRefreshKey += 1;
          }
        }
      }

      if (
        type === 'candidate' &&
        (!state.applicantSelectedJob ||
          state.applicantSelectedJob.job_id !== action.payload.newChat.job_id ||
          !isChatPage)
      ) {
        state.applicantJobsUnreadMessageCount =
          (state.applicantJobsUnreadMessageCount ?? 0) + 1;
      }
    },
    // Floating Chatbox
    decrementFloatingChatUnreadCount: (
      state,
      action: PayloadAction<FloatingChatUnreadCountDecrementPayload>,
    ) => {
      if (
        action.payload.type === 'direct' &&
        state.floatingChatBox.unreadCounts?.direct_unread
      ) {
        const prevCount = state.floatingChatBox.unreadCounts.direct_unread;
        state.floatingChatBox.unreadCounts.direct_unread = Math.max(
          0,
          prevCount - action.payload.count,
        );
      } else if (
        action.payload.type === 'group' &&
        state.floatingChatBox.unreadCounts?.group_unread
      ) {
        const prevCount = state.floatingChatBox.unreadCounts.group_unread;
        state.floatingChatBox.unreadCounts.group_unread = Math.max(
          0,
          prevCount - action.payload.count,
        );
      } else if (
        action.payload.type === 'support' &&
        state.floatingChatBox.unreadCounts?.support_unread
      ) {
        const prevCount = state.floatingChatBox.unreadCounts.support_unread;
        state.floatingChatBox.unreadCounts.support_unread = Math.max(
          0,
          prevCount - action.payload.count,
        );
      }

      if (state.floatingChatBox.unreadCounts?.total_unread) {
        const prevTotal = state.floatingChatBox.unreadCounts.total_unread;
        state.floatingChatBox.unreadCounts.total_unread = Math.max(
          0,
          prevTotal - action.payload.count,
        );
      }
    },
    toggleFloatingChatBox: (state) => {
      const newValue = !state.floatingChatBox.isOpen;
      if (!newValue) {
        state.floatingChatBox.selectedChatItems = null;
        state.floatingChatBox.selectedTeamChatItems = null;
      }
      state.floatingChatBox.isOpen = !state.floatingChatBox.isOpen;
    },
    toggleChatView: (state, action: PayloadAction<ChatToggleView>) => {
      if (action.payload) {
        state.floatingChatBox.chatToggleView = action.payload;
      }
    },
    updateSearchQuery: (state, action) => {
      state.floatingChatBox.searchQuery = action.payload;
    },
    updateIsChatBubbleVisible: (state, action) => {
      state.floatingChatBox.isChatBubbleVisible = action.payload;
    },
    // Desktop: open the left drawer directly on a channel's list (no toggle).
    openChatDrawer: (state, action: PayloadAction<ChatToggleView>) => {
      state.floatingChatBox.chatToggleView = action.payload;
      state.floatingChatBox.screen = 'home';
      state.floatingChatBox.isDrawerOpen = true;
      state.floatingChatBox.selectedChat = null;
      state.floatingChatBox.selectedChatItems = null;
      state.floatingChatBox.selectedTeamChat = null;
      state.floatingChatBox.selectedTeamChatItems = null;
    },
    closeChatDrawer: (state) => {
      state.floatingChatBox.isDrawerOpen = false;
      state.floatingChatBox.screen = 'home';
      state.floatingChatBox.selectedChat = null;
      state.floatingChatBox.selectedChatItems = null;
      state.floatingChatBox.selectedTeamChat = null;
      state.floatingChatBox.selectedTeamChatItems = null;
    },
    updateChatScreen: (
      state,
      action: PayloadAction<ChatToggleView | 'home'>,
    ) => {
      if (action.payload === 'Support messages') {
        state.floatingChatBox.screen = 'supportChat';
      } else if (action.payload === 'Team messages') {
        state.floatingChatBox.screen = 'teamChat';
      } else {
        state.floatingChatBox.screen = 'home';
      }
    },
    updateEmployerSelectedChat: (
      state,
      action: PayloadAction<SupportChat | null>,
    ) => {
      if (!action.payload) {
        state.floatingChatBox.selectedChat = null;
        return;
      }
      state.floatingChatBox.selectedChat = {
        ...action.payload,
        unread_message: 0,
      };
      state.floatingChatBox?.supportChats?.support_chat_details?.map((chat) => {
        if (chat.support_chat_id === action.payload?.support_chat_id) {
          chat.unread_message = 0;
        }
        return chat;
      });
      state.floatingChatBox.selectedChatItems = null;
    },
    updateEmployerSupportChats: (
      state,
      action: PayloadAction<SupportChatListResponse>,
    ) => {
      state.floatingChatBox.supportChats = action.payload;
    },
    employerSupportTeamChatSeenSocket: (
      state,
      action: PayloadAction<ChatMessageResponse>,
    ) => {
      if (
        action.payload.support_chat_id &&
        state.floatingChatBox.selectedChat?.support_chat_id ===
          action.payload.support_chat_id &&
        state.floatingChatBox.selectedChatItems
      ) {
        state.floatingChatBox.selectedChatItems.data =
          state.floatingChatBox.selectedChatItems.data?.map((chat) => {
            if (
              !chat.is_chat_seen &&
              isDateBeforeOrEqual(chat.created_at, action.payload.created_at)
            ) {
              return { ...chat, is_chat_seen: true };
            }
            return chat;
          });
      } else if (
        (state.floatingChatBox.selectedTeamChat?.to_chat_group_id ===
          action.payload.to_chat_group_id ||
          state.floatingChatBox.selectedTeamChat?.to_user_id ===
            action.payload.to_user_id) &&
        state.floatingChatBox.selectedTeamChatItems?.data
      ) {
        state.floatingChatBox.selectedTeamChatItems.data =
          state.floatingChatBox.selectedTeamChatItems.data?.map((chat) => {
            if (
              !chat.is_chat_seen &&
              isDateBeforeOrEqual(chat.created_at, action.payload.created_at)
            ) {
              return { ...chat, is_chat_seen: true };
            }
            return chat;
          });
      }
    },
    employerNewGroupCreateSocket: (state, action) => {
      if (action.payload.is_group_admin) {
        return;
      }
      if (state.floatingChatBox.teamChats?.latest_chat_details) {
        const newGroup: LatestChatList = {
          ...action.payload,
          unseen_chat_count: 1,
        };
        state.floatingChatBox.teamChats.latest_chat_details.unshift(newGroup);
      }
    },
    employerNewSupportTeamMessageRecievedSocket: (
      state,
      action: PayloadAction<ChatMessageResponse>,
    ) => {
      if (action.payload.support_chat_id) {
        if (
          state.floatingChatBox.supportChats.support_chat_details &&
          (action.payload.support_chat_id !==
            state.floatingChatBox.selectedChat?.support_chat_id ||
            state.floatingChatBox.screen !== 'supportChat')
        ) {
          let isFound = false;

          state.floatingChatBox.supportChats.support_chat_details =
            state.floatingChatBox.supportChats.support_chat_details.map(
              (supportChat) => {
                if (
                  supportChat.support_chat_id === action.payload.support_chat_id
                ) {
                  isFound = true;
                  return {
                    ...supportChat,
                    latest_message: action.payload.message,
                    unread_message: (supportChat.unread_message ?? 0) + 1,
                  };
                }
                return supportChat;
              },
            );
          if (!isFound) {
            state.floatingChatBox.supportChats.support_chat_details.unshift({
              support_chat_id: action.payload.support_chat_id,
              org_id: action.payload.org_id,
              latest_message: action.payload.message,
              unread_message: 1,
              name: action.payload.name,
              picture: action.payload.picture,
              created_at: action.payload.created_at,
              is_resolved: false,
            });
          }

          if (state.floatingChatBox.unreadCounts) {
            state.floatingChatBox.unreadCounts.support_unread =
              (state.floatingChatBox.unreadCounts.support_unread ?? 0) + 1;
            state.floatingChatBox.unreadCounts.total_unread =
              (state.floatingChatBox.unreadCounts.total_unread ?? 0) + 1;
          }
        }

        if (
          state.floatingChatBox.selectedChat?.support_chat_id ===
            action.payload.support_chat_id &&
          state.floatingChatBox.selectedChatItems?.data
        ) {
          if (
            unshiftMessageIfNew(
              state.floatingChatBox.selectedChatItems.data,
              action.payload,
            )
          ) {
            state.floatingChatBox.selectedChatItems.total_chat_count =
              (state.floatingChatBox.selectedChatItems.total_chat_count ?? 0) +
              1;
          }
        }
      } else {
        if (
          state.floatingChatBox.teamChats.latest_chat_details &&
          ((action.payload.to_user_id !==
            state.floatingChatBox.selectedTeamChat?.to_user_id &&
            action.payload.to_chat_group_id !==
              state.floatingChatBox.selectedTeamChat?.to_chat_group_id) ||
            state.floatingChatBox.screen !== 'teamChat')
        ) {
          let isFound = false;

          state.floatingChatBox.supportChats.support_chat_details =
            state.floatingChatBox.teamChats.latest_chat_details.map(
              (teamChat) => {
                if (
                  teamChat.to_chat_group_id ===
                    action.payload.to_chat_group_id ||
                  teamChat.to_user_id === action.payload.to_user_id
                ) {
                  isFound = true;
                  return {
                    ...teamChat,
                    latest_message: action.payload.message,
                    unseen_chat_count: (teamChat.unseen_chat_count ?? 0) + 1,
                  };
                }
                return teamChat;
              },
            );
          if (!isFound) {
            state.floatingChatBox.teamChats.latest_chat_details.unshift({
              unseen_chat_count: 1,
              to_chat_group_id: action.payload.to_chat_group_id,
              to_user_id: action.payload.to_user_id,
              message: action.payload.message,
              picture: action.payload.picture,
              from_user_id: action.payload.from_user_id,
              created_at: action.payload.created_at,
            });
          }

          if (
            action.payload.to_chat_group_id &&
            state.floatingChatBox.unreadCounts
          ) {
            state.floatingChatBox.unreadCounts.group_unread =
              (state.floatingChatBox.unreadCounts.group_unread ?? 0) + 1;
            state.floatingChatBox.unreadCounts.total_unread =
              (state.floatingChatBox.unreadCounts.total_unread ?? 0) + 1;
          } else if (
            action.payload.to_user_id &&
            state.floatingChatBox.unreadCounts
          ) {
            state.floatingChatBox.unreadCounts.direct_unread =
              (state.floatingChatBox.unreadCounts.direct_unread ?? 0) + 1;
            state.floatingChatBox.unreadCounts.total_unread =
              (state.floatingChatBox.unreadCounts.total_unread ?? 0) + 1;
          }
        }

        if (
          (state.floatingChatBox.selectedTeamChat?.to_user_id ===
            action.payload.to_user_id ||
            state.floatingChatBox.selectedTeamChat?.to_chat_group_id ===
              action.payload.to_chat_group_id) &&
          state.floatingChatBox.selectedTeamChatItems?.data
        ) {
          if (
            unshiftMessageIfNew(
              state.floatingChatBox.selectedTeamChatItems.data,
              action.payload,
            )
          ) {
            state.floatingChatBox.selectedTeamChatItems.total_chat_count =
              (state.floatingChatBox.selectedTeamChatItems.total_chat_count ??
                0) + 1;
          }
        }
      }
    },
    employerSupportResolvedSocket: (
      state,
      action: PayloadAction<ChatMessageResponse>,
    ) => {
      const { payload } = action;
      if (payload?.support_chat_id) {
        state.floatingChatBox.supportChats?.support_chat_details?.map(
          (chat) => {
            if (chat?.support_chat_id === payload?.support_chat_id) {
              chat.is_resolved = true;
            }
            return chat;
          },
        );
        if (
          state.floatingChatBox?.selectedChat?.support_chat_id ===
          payload.support_chat_id
        ) {
          state.floatingChatBox.selectedChat.is_resolved = true;
        }
      }
    },
    updateSelectedGroupMembers: (
      state,
      action: PayloadAction<GetChatGroupUsersResponse[] | undefined>,
    ) => {
      state.floatingChatBox.selectedTeamChatMembers = action.payload;
    },
    updateGroupChats: (
      state,
      action: PayloadAction<LatestChatListResponse>,
    ) => {
      state.floatingChatBox.teamChats = action.payload;
    },
    updateSelectedGroupChat: (
      state,
      action: PayloadAction<LatestChatList | null>,
    ) => {
      if (!action.payload) {
        state.floatingChatBox.selectedTeamChat = null;
        return;
      }
      state.floatingChatBox.selectedTeamChat = {
        ...action.payload,
        unseen_chat_count: 0,
      };
      state.floatingChatBox?.teamChats?.latest_chat_details?.forEach((chat) => {
        if (chat.id === action.payload?.id) {
          chat.unseen_chat_count = 0;
        }
      });
      state.floatingChatBox.selectedTeamChatItems = null;
    },
    updateGroupData: (
      state,
      action: PayloadAction<{
        groupId: string;
        data: { newGroupName?: string };
      }>,
    ) => {
      const { groupId, data } = action.payload;

      state?.floatingChatBox?.teamChats?.latest_chat_details?.forEach(
        (chat) => {
          if (chat?.to_chat_group_id === groupId) {
            chat.group_name = data.newGroupName;
          }
        },
      );
    },
    updateStateForInitiateDirectChat: (
      state,
      action: PayloadAction<{
        userDetails: GetOrganizationUsersListResponse;
        currUserId: string;
      }>,
    ) => {
      const { payload } = action;

      const directChatExist =
        state.floatingChatBox.teamChats?.latest_chat_details?.some(
          (chat) =>
            !chat?.to_chat_group_id &&
            ((chat.to_user_id === payload.userDetails.user_id &&
              chat.from_user_id === payload.currUserId) ||
              (chat.from_user_id === payload.userDetails.user_id &&
                chat.to_user_id === payload.currUserId)),
        );

      if (directChatExist) {
        state.floatingChatBox.selectedTeamChat =
          state.floatingChatBox.teamChats.latest_chat_details?.find(
            (chat) =>
              !chat?.to_chat_group_id &&
              ((chat.to_user_id === payload.userDetails.user_id &&
                chat.from_user_id === payload.currUserId) ||
                (chat.from_user_id === payload.userDetails.user_id &&
                  chat.to_user_id === payload.currUserId)),
          ) ?? null;
      } else {
        state.floatingChatBox.selectedTeamChat = {
          name: payload.userDetails.full_name,
          picture: payload.userDetails.picture,
          unseen_chat_count: 0,
          to_user_id: payload.userDetails.user_id,
          from_user_id: payload.currUserId,
        };
      }
      state.floatingChatBox.selectedTeamChatItems = null;
      state.floatingChatBox.screen = 'teamChat';
    },

    // admin chat
    setAdminSupportChatFilter: (
      state,
      action: PayloadAction<AdminChatFilters>,
    ) => {
      state.adminChat.filters = action.payload;
      state.adminChat.supportChats = {
        data: [],
        page_no: 1,
        page_size: 10,
        total_pages: 1,
      };
      state.adminChat.selectedChat = null;
      state.adminChat.selectedChatItems = null;
    },
    updateAdminSelectedChat: (
      state,
      action: PayloadAction<SupportChat | null>,
    ) => {
      if (!action.payload) {
        state.adminChat.selectedChat = null;
        state.adminChat.selectedChatItems = null;
        return;
      }
      state.adminChat.selectedChat = {
        ...action.payload,
        unread_message: 0,
      };
      state.adminChat.selectedChatItems = null;
      state.adminChat?.supportChats?.data?.map((chat) => {
        if (chat.support_chat_id === action.payload?.support_chat_id) {
          chat.unread_message = 0;
        }
        return chat;
      });
    },
    updateAdminSelectedChatItems: (
      state,
      action: PayloadAction<ChatMessageListResponse>,
    ) => {
      state.adminChat.selectedChatItems = action.payload;
    },
    updateAdminSupportChats: (
      state,
      action: PayloadAction<AdminSupportChatMessageListResponse>,
    ) => {
      state.adminChat.supportChats = action.payload;
    },
    updateAdminChatStats: (state, action: PayloadAction<SupportChatCount>) => {
      state.adminChat.chatStats = action.payload;
    },
    adminChatSeenSocket: (
      state,
      action: PayloadAction<ChatMessageResponse>,
    ) => {
      if (
        state.adminChat.selectedChat?.support_chat_id ===
          action.payload.support_chat_id &&
        state.adminChat.selectedChatItems?.data
      ) {
        state.adminChat.selectedChatItems.data =
          state.adminChat.selectedChatItems.data.map((chat) => {
            if (
              !chat.is_chat_seen &&
              isDateBeforeOrEqual(chat.created_at, action.payload.created_at)
            ) {
              return { ...chat, is_chat_seen: true };
            }
            return chat;
          });
      }
    },
    adminNewMessageRecievedSocket: (
      state,
      action: PayloadAction<ChatMessageResponse>,
    ) => {
      const { payload } = action;

      const selectedChatId = payload.support_chat_id;

      if (
        state.adminChat.selectedChat?.support_chat_id ===
          payload.support_chat_id &&
        state.adminChat.selectedChatItems
      ) {
        if (
          unshiftMessageIfNew(state.adminChat.selectedChatItems.data, payload)
        ) {
          state.adminChat.selectedChatItems.total_chat_count =
            (state.adminChat.selectedChatItems.total_chat_count ?? 0) + 1;
        }
      }

      if (state.adminChat.supportChats?.data) {
        let found = false;
        let updatedChat: SupportChat | null = null;

        state.adminChat.supportChats.data =
          state.adminChat.supportChats.data.reduce<SupportChat[]>(
            (acc, chat) => {
              if (chat.support_chat_id === selectedChatId) {
                found = true;
                updatedChat = {
                  ...chat,
                  ...asChatListEntry(payload),
                  latest_message: payload.message,
                  unread_message:
                    state.adminChat.selectedChatItems?.data?.[0]
                      ?.support_chat_id === selectedChatId
                      ? 0
                      : chat.unread_message
                        ? chat.unread_message + 1
                        : 1,
                };
                return acc;
              }

              acc.push(chat);
              return acc;
            },
            [],
          );

        if (found && updatedChat) {
          state.adminChat.supportChats.data.unshift(updatedChat);
        }

        if (!found) {
          state.adminChat.supportChats.data.unshift({
            ...asChatListEntry(payload),
            user_id: payload?.to_user_id || payload?.from_user_id,
            latest_message: payload.message,
            unread_message:
              state.adminChat.supportChats?.data?.[0]?.support_chat_id ===
              selectedChatId
                ? 0
                : 1,
          });
        }
      }
    },
    resetEmployerChat: (state) => {
      state.employerChatApplicants = null;
      state.employerChatTemplates = null;
      state.employerSelectedApplicant = null;
      state.employerSelectedJobDetails = null;
      state.employerSelectedJobIdOrgId = null;
      state.chats = null;
    },
    resetFloatingChatOnOrgChange: (state) => {
      state.floatingChatBox.selectedChat = null;
      state.floatingChatBox.selectedChatItems = null;
      state.floatingChatBox.selectedTeamChat = null;
      state.floatingChatBox.selectedTeamChatItems = null;
      state.floatingChatBox.screen = 'home';
    },
    resetApplicantChat: (state) => {
      state.applicantChatJobs = null;
      state.applicantSelectedJob = null;
      state.applicantJobDetails = null;
      state.chats = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchApplicantChatJobs.pending, (state, action) => {
        state.isFetchingApplicantChatJobs = true;
        if (action.meta.arg.shouldSelectFirst) {
          state.applicantSelectedJob = null;
        }
      })
      .addCase(fetchApplicantChatJobs.fulfilled, (state, action) => {
        if (action.payload.page_no === 1) {
          state.applicantChatJobs = action.payload;
        } else if (state.applicantChatJobs?.company_details) {
          state.applicantChatJobs.company_details =
            state.applicantChatJobs.company_details.concat(
              action.payload.company_details ?? [],
            );
          state.applicantChatJobs.page_no = action.payload.page_no;
          state.applicantChatJobs.page_size = action.payload.page_size;
          state.applicantChatJobs.total_pages = action.payload.total_pages;
        }

        if (
          action.meta.arg.shouldSelectFirst &&
          state.applicantChatJobs?.company_details?.length
        ) {
          state.applicantJobDetails = null;
          state.chats = null;
          const firstApplicant = state.applicantChatJobs?.company_details?.[0];
          state.applicantSelectedJob = firstApplicant;
          state.applicantJobsUnreadMessageCount = Math.max(
            0,
            (state.applicantJobsUnreadMessageCount ?? 0) -
              (firstApplicant.unread_message_count ?? 0),
          );
          if (
            state.applicantChatJobs.company_details?.[0]?.unread_message_count
          ) {
            state.applicantChatJobs.company_details[0].unread_message_count = 0;
          }
        }
        state.isFetchingApplicantChatJobs = false;
      });
    builder.addCase(fetchChats.fulfilled, (state, action) => {
      // No cursor = first page = replace. Without this, a remount with
      // preserved state.chats concats the same page onto itself.
      if (!action.meta.arg.cursor) {
        state.chats = action.payload;
      } else if (state.chats) {
        state.chats.all_chats = state.chats.all_chats?.concat(
          action.payload.all_chats ?? [],
        );
        state.chats.all_users = action.payload.all_users;
        state.chats.next_cursor = action.payload.next_cursor;
        state.chats.total_chat_count = action.payload.total_chat_count;
        state.chats.page_size = action.payload.page_size;
      }
    });
    builder.addCase(chatBlockUnblock.fulfilled, (state) => {
      if (state.chats) {
        state.chats.is_chat_blocked = !state.chats.is_chat_blocked;
      }
    });
    builder.addCase(sendMessage.fulfilled, (state, action) => {
      const sender = state.chats?.all_users?.find(
        (chatUser) => chatUser.user_id === action.payload.from_user_id,
      );

      let inserted = false;
      if (state.chats?.all_chats && sender) {
        inserted = unshiftMessageIfNew(state.chats.all_chats, action.payload);
      }

      if (state.chats && inserted) {
        state.chats.total_chat_count = (state.chats.total_chat_count ?? 0) + 1;
      }

      // update last message for candidate jobs if senderType is Applicant
      if (
        action.meta.arg.senderType === 'Applicant' &&
        state.applicantChatJobs?.company_details
      ) {
        state.applicantChatJobs.company_details =
          state.applicantChatJobs.company_details.map((job) => {
            if (job.job_id === action.payload.job_id) {
              return { ...job, latest_message: action.payload.message };
            }
            return job;
          });
      }

      // update last message for job applicant if senderType is Employer
      if (
        action.meta.arg.senderType === 'Employer' &&
        state.employerChatApplicants?.all_applicants
      ) {
        state.employerChatApplicants.all_applicants =
          state.employerChatApplicants.all_applicants.map((applicant) => {
            if (applicant.user?.id === action.meta.arg.applicantUserId) {
              return {
                ...applicant,
                latest_message: action.payload.message,
              };
            }
            return applicant;
          });
      }
    });

    builder
      .addCase(fetchChatApplicants.pending, (state) => {
        state.isFetchingEmployerChatApplicants = true;
      })
      .addCase(fetchChatApplicants.fulfilled, (state, action) => {
        state.employerChatApplicants = action.payload;
        state.isFetchingEmployerChatApplicants = false;
      });
    builder
      .addCase(fetchOrgApplicantChats.pending, (state, action) => {
        // Page 1 (or unspecified) is a fresh load → full-panel loader; later
        // pages keep the list visible with a bottom "loading more" spinner.
        if ((action.meta.arg.pageNo ?? 1) > 1) {
          state.isFetchingOrgApplicantChatsMore = true;
        } else {
          state.isFetchingOrgApplicantChats = true;
        }
      })
      .addCase(fetchOrgApplicantChats.fulfilled, (state, action) => {
        // Drop a response whose query no longer matches the active filter — a
        // stale page that resolved after the user changed the search would
        // otherwise get appended onto (or replace) the current results.
        if ((action.meta.arg.query ?? '') !== (state.filters.query ?? '')) {
          return;
        }
        const isNextPage = (action.meta.arg.pageNo ?? 1) > 1;
        if (isNextPage && state.orgApplicantChats) {
          state.orgApplicantChats = {
            ...action.payload,
            applicants: [
              ...(state.orgApplicantChats.applicants ?? []),
              ...(action.payload.applicants ?? []),
            ],
          };
        } else {
          state.orgApplicantChats = action.payload;
        }
        state.isFetchingOrgApplicantChats = false;
        state.isFetchingOrgApplicantChatsMore = false;
      })
      .addCase(fetchOrgApplicantChats.rejected, (state, action) => {
        // A superseded (aborted) request must not clear the loading flags —
        // the newer request that aborted it owns them and its own
        // pending/fulfilled will resolve them.
        if (action.meta.aborted) {
          return;
        }
        state.isFetchingOrgApplicantChats = false;
        state.isFetchingOrgApplicantChatsMore = false;
      });
    builder
      .addCase(fetchOrgChatJobs.pending, (state) => {
        state.isFetchingOrgChatJobs = true;
      })
      .addCase(fetchOrgChatJobs.fulfilled, (state, action) => {
        state.orgChatJobs = action.payload.jobs ?? [];
        state.isFetchingOrgChatJobs = false;
      })
      .addCase(fetchOrgChatJobs.rejected, (state) => {
        state.isFetchingOrgChatJobs = false;
      });
    builder.addCase(
      fetchEmployerChatTemplateList.fulfilled,
      (state, action) => {
        if (action.payload.page === 1) {
          state.employerChatTemplates = action.payload;
        } else if (state.employerChatTemplates?.all_chat_templates) {
          state.employerChatTemplates.all_chat_templates =
            state.employerChatTemplates.all_chat_templates.concat(
              action.payload.all_chat_templates ?? [],
            );
          state.employerChatTemplates.page = action.payload.page;
          state.employerChatTemplates.page_size = action.payload.page_size;
          state.employerChatTemplates.total_pages = action.payload.total_pages;
        }
      },
    );
    builder.addCase(deleteEmployerChatTemplate.fulfilled, (state, action) => {
      if (state.employerChatTemplates?.all_chat_templates) {
        state.employerChatTemplates.all_chat_templates =
          state.employerChatTemplates.all_chat_templates.filter(
            (template) => template.id !== action.meta.arg.templateId,
          );
      }
    });
    builder.addCase(createEmployerChatTemplate.fulfilled, (state, action) => {
      if (state.employerChatTemplates?.all_chat_templates) {
        state.employerChatTemplates.all_chat_templates.push(action.payload);
      }
    });
    builder.addCase(fetchPostedJobDetails.fulfilled, (state, action) => {
      // Drop stale responses: rapid job switching (e.g. repeated "Messages"
      // nav clicks) can fire this for a job the user has since navigated
      // away from, and out-of-order resolution would otherwise let a
      // superseded fetch overwrite the currently-selected job's details.
      if (action.meta.arg.jobId !== state.employerSelectedJobIdOrgId?.jobId) {
        return;
      }
      state.employerSelectedJobDetails = action.payload;
    });
    builder.addCase(updateJobChat.fulfilled, (state, action) => {
      if (state.chats?.all_chats) {
        state.chats.all_chats = state.chats.all_chats.map((chat) => {
          if (chat.id === action.payload.id) {
            return action.payload;
          }
          return chat;
        });
      }
    });
    builder.addCase(deleteJobChat.fulfilled, (state, action) => {
      if (state.chats?.all_chats) {
        state.chats.all_chats = state.chats.all_chats.filter(
          (chat) => chat.id !== action.meta.arg.chatId,
        );
        state.chats.total_chat_count = (state.chats.total_chat_count ?? 0) - 1;
      }
    });
    builder.addCase(addChatReaction.fulfilled, (state, action) => {
      if (state.chats?.all_chats) {
        state.chats.all_chats = state.chats.all_chats.map((chat) => {
          if (chat.id === action.payload.chat_id) {
            const updatedChat = { ...chat };
            updatedChat.reactions?.push(action.payload);
            return updatedChat;
          }
          return chat;
        });
      }
    });
    builder.addCase(updateChatReaction.fulfilled, (state, action) => {
      if (state.chats?.all_chats) {
        state.chats.all_chats = state.chats.all_chats.map((chat) => {
          if (chat.id === action.payload.chat_id) {
            const updatedChat = { ...chat };
            updatedChat.reactions = updatedChat.reactions?.map((reaction) => {
              if (reaction.id === action.payload.id) {
                return action.payload;
              }
              return reaction;
            });
            return updatedChat;
          }
          return chat;
        });
      }
    });
    builder.addCase(deleteChatReaction.fulfilled, (state, action) => {
      if (state.chats?.all_chats) {
        state.chats.all_chats = state.chats.all_chats.map((chat) => {
          if (chat.id === action.meta.arg.chatId) {
            const updatedChat = { ...chat };
            updatedChat.reactions = updatedChat.reactions?.filter(
              (reaction) => reaction.id !== action.meta.arg.reactionId,
            );
            return updatedChat;
          }
          return chat;
        });
      }
    });
    builder.addCase(updateEmployerChatTemplate.fulfilled, (state, action) => {
      if (state.employerChatTemplates?.all_chat_templates) {
        state.employerChatTemplates.all_chat_templates =
          state.employerChatTemplates.all_chat_templates.map((template) => {
            if (template.id === action.payload.id) {
              return action.payload;
            }
            return template;
          });
      }
    });
    builder.addCase(jobByIdOrAliasForCandidate.fulfilled, (state, action) => {
      state.applicantJobDetails = action.payload;
    });
    builder.addCase(rejectJobInvite.fulfilled, (state, action) => {
      if (
        state.applicantSelectedJob?.job_id === action.meta.arg.jobId &&
        state.chats
      ) {
        state.chats.invite_status = 'Rejected';
      }
    });

    // Floating Chat

    builder
      .addCase(getSupportMessages.pending, (state) => {
        state.floatingChatBox.isLoading = true;
      })
      .addCase(getSupportMessages.fulfilled, (state, action) => {
        const { payload } = action;
        const { supportChats } = state.floatingChatBox;

        if (payload.page_no === 1) {
          state.floatingChatBox.supportChats = payload;
        } else if (supportChats.support_chat_details) {
          supportChats.support_chat_details =
            supportChats.support_chat_details.concat(
              payload.support_chat_details ?? [],
            );
          supportChats.page_no = payload.page_no;
          supportChats.page_size = payload.page_size;
          supportChats.total_pages = payload.total_pages;
        }

        state.floatingChatBox.isLoading = false;
      })
      .addCase(getSupportMessages.rejected, (state) => {
        state.floatingChatBox.isLoading = false;
      });

    builder
      .addCase(getChatMessages.pending, (state) => {
        state.floatingChatBox.isLoading = true;
      })
      .addCase(getChatMessages.fulfilled, (state, action) => {
        const { arg } = action.meta;
        const { payload } = action;
        state.floatingChatBox.isLoading = false;

        if (!arg.supportChatId) return;

        // Drop stale responses when the user switched support chats mid-flight.
        const selectedSupportId =
          state.floatingChatBox.selectedChat?.support_chat_id;
        if (selectedSupportId && arg.supportChatId !== selectedSupportId) {
          return;
        }

        const chatItems = state.floatingChatBox.selectedChatItems;

        // No cursor = first page = replace. Cursor = pagination = append.
        if (!arg.cursor) {
          state.floatingChatBox.selectedChatItems = payload;
        } else if (chatItems) {
          chatItems.data = chatItems.data?.concat(payload.data ?? []);
          chatItems.next_cursor = payload.next_cursor;
          chatItems.page_size = payload.page_size;
          chatItems.total_chat_count = payload.total_chat_count;
        }
      })
      .addCase(getChatMessages.rejected, (state) => {
        state.floatingChatBox.isLoading = false;
      });

    builder.addCase(sendChatMessage.fulfilled, (state, action) => {
      if (action.payload) {
        const arg = action.meta.arg.body as ChatMessageBody & {
          is_support?: boolean;
        };
        const wasDraft = !!arg?.is_support && !arg?.support_chat_id;
        if (wasDraft && state.floatingChatBox.selectedChat) {
          // First message on a fresh support thread: adopt the id the backend
          // just created and seed an empty thread so the message renders in
          // place of the topic picker (no support_chat row existed before now).
          state.floatingChatBox.selectedChat.support_chat_id =
            action.payload.support_chat_id ?? undefined;
          if (!state.floatingChatBox.selectedChatItems) {
            state.floatingChatBox.selectedChatItems = {
              data: [],
              total_chat_count: 0,
            } as any;
          }
        }
        unshiftMessageIfNew(
          state.floatingChatBox.selectedChatItems?.data,
          action.payload,
        );

        const { supportChats, selectedChat } = state.floatingChatBox;
        const selectedChatId = action.payload.support_chat_id;

        if (supportChats?.support_chat_details) {
          supportChats.support_chat_details =
            supportChats.support_chat_details.filter(
              (item) => item.support_chat_id !== selectedChatId,
            );

          const updatedChat: SupportChat = {
            ...state.floatingChatBox.selectedChat!,
            created_at: action.payload.created_at,
            latest_message: action.payload.message,
            is_resolved: false,
          };

          supportChats.support_chat_details.unshift(updatedChat);
          if (selectedChat?.is_resolved) {
            selectedChat.is_resolved = false;
          }
        }
      }
    });

    // Group Chats

    builder
      .addCase(getFrontChatList.pending, (state, action) => {
        if (action.meta.arg.showLoader) state.floatingChatBox.isLoading = true;
      })
      .addCase(getFrontChatList.fulfilled, (state, action) => {
        const { payload } = action;
        const { teamChats: groupChats } = state.floatingChatBox;

        if (payload.page_no === 1) {
          state.floatingChatBox.teamChats = payload;
        } else if (groupChats.latest_chat_details) {
          groupChats.latest_chat_details =
            groupChats.latest_chat_details.concat(
              payload.latest_chat_details ?? [],
            );
          groupChats.page_no = payload.page_no;
          groupChats.page_size = payload.page_size;
          groupChats.total_pages = payload.total_pages;
        }

        state.floatingChatBox.isLoading = false;
      })
      .addCase(getFrontChatList.rejected, (state) => {
        state.floatingChatBox.isLoading = false;
      });

    builder.addCase(fetchGroupChatItems.pending, (state) => {
      state.floatingChatBox.isLoading = true;
    });
    builder.addCase(fetchGroupChatItems.fulfilled, (state, action) => {
      const { arg } = action.meta;
      const { payload } = action;
      state.floatingChatBox.isLoading = false;

      if (!arg.chatGroupId && !arg.userId) return;

      // Drop stale responses when the user switched team chats mid-flight.
      const { selectedTeamChat } = state.floatingChatBox;
      if (
        selectedTeamChat &&
        ((arg.chatGroupId &&
          arg.chatGroupId !== selectedTeamChat.to_chat_group_id) ||
          (arg.userId && arg.userId !== selectedTeamChat.to_user_id))
      ) {
        return;
      }

      const chatItems = state.floatingChatBox.selectedTeamChatItems;

      // No cursor = first page = replace. Cursor = pagination = append.
      if (!arg.cursor) {
        state.floatingChatBox.selectedTeamChatItems = payload;
      } else if (chatItems) {
        chatItems.data = chatItems.data?.concat(payload.data ?? []);
        chatItems.next_cursor = payload.next_cursor;
        chatItems.page_size = payload.page_size;
        chatItems.total_chat_count = payload.total_chat_count;
      }
    });

    builder.addCase(sendGroupChat.fulfilled, (state, action) => {
      const {
        payload,
        meta: { arg },
      } = action;
      if (payload) {
        unshiftMessageIfNew(state.floatingChatBox.selectedTeamChatItems?.data, {
          ...payload,
          name: state.floatingChatBox?.selectedTeamChat?.name,
        });

        const { teamChats: groupChats } = state.floatingChatBox;
        const selectedChatId = payload.to_chat_group_id || arg.userName;

        if (groupChats?.latest_chat_details) {
          groupChats.latest_chat_details =
            groupChats.latest_chat_details.filter((item) => {
              if (item.to_chat_group_id) {
                return item.to_chat_group_id !== selectedChatId;
              }
              if (item.name) {
                return item.name !== selectedChatId;
              }
              return true;
            });

          const updatedChat = {
            ...state.floatingChatBox.selectedTeamChat!,
            ...asChatListEntry(payload),
          };

          groupChats.latest_chat_details.unshift(updatedChat);
        }
      }
    });

    builder.addCase(initiateDirectChat.fulfilled, (state, action) => {
      const {
        payload,
        meta: {
          arg: { userDetails },
        },
      } = action;

      const { teamChats: groupChats } = state.floatingChatBox;
      const selectedChatId = payload.to_user_id;

      if (groupChats?.latest_chat_details) {
        const directChatExist = groupChats.latest_chat_details.some(
          (chat) => chat.to_user_id === selectedChatId,
        );

        if (!directChatExist) {
          groupChats.latest_chat_details.unshift({
            ...asChatListEntry(payload),
            name: userDetails?.full_name,
            picture: userDetails?.picture,
          });
        } else {
          groupChats.latest_chat_details =
            groupChats.latest_chat_details.filter(
              (chat) => chat.to_user_id !== selectedChatId,
            );

          const updatedChat = {
            ...asChatListEntry(payload),
            name: userDetails?.full_name,
            picture: userDetails?.picture,
          };

          groupChats.latest_chat_details.unshift(updatedChat);
        }

        state.floatingChatBox.selectedTeamChat = {
          ...asChatListEntry(payload),
          name: userDetails?.full_name,
          picture: userDetails?.picture,
        };
        state.floatingChatBox.selectedTeamChatItems = {
          data: [
            {
              ...payload,
              name: userDetails?.full_name,
              picture: userDetails?.picture,
            },
          ],
          page_size: 10,
        };

        state.floatingChatBox.screen = 'teamChat';
      }
    });

    builder.addCase(exitGroup.fulfilled, (state, action) => {
      const {
        meta: {
          arg: { groupId },
        },
      } = action;
      const { teamChats: groupChats } = state.floatingChatBox;

      if (groupChats?.latest_chat_details) {
        groupChats.latest_chat_details = groupChats.latest_chat_details.filter(
          (chat) => chat.to_chat_group_id !== groupId,
        );
      }
    });

    // Admin Chats
    builder.addCase(getAdminSupportMessages.pending, (state, action) => {
      if (!action.meta.arg.pageNo || action.meta.arg.pageNo <= 1) {
        state.adminChat.isSupportMessagesLoading = true;
      }
    });
    builder
      .addCase(getAdminSupportMessages.fulfilled, (state, action) => {
        const { payload, meta } = action;

        if (payload.page_no === 1) {
          state.adminChat.supportChats = payload;
        } else if (state.adminChat.supportChats?.data) {
          state.adminChat.supportChats.data =
            state.adminChat.supportChats.data.concat(payload.data ?? []);
          state.adminChat.supportChats.page_no = payload.page_no;
          state.adminChat.supportChats.page_size = payload.page_size;
          state.adminChat.supportChats.total_pages = payload.total_pages;
        }

        if (
          (meta.arg.selectedSupportId &&
            state.adminChat.selectedChat?.support_chat_id !==
              meta.arg.selectedSupportId) ||
          meta.arg.fallbackToFirst
        ) {
          let selectedChat =
            state.adminChat.supportChats?.data?.find(
              (val) => val.support_chat_id === meta.arg.selectedSupportId,
            ) ?? null;

          if (!selectedChat && action.meta.arg.fallbackToFirst) {
            selectedChat = state.adminChat.supportChats?.data?.[0] ?? null;
          }
          state.adminChat.selectedChat = selectedChat;
        }

        state.adminChat.isSupportMessagesLoading = false;
      })
      .addCase(getAdminSupportMessages.rejected, (state) => {
        state.adminChat.isSupportMessagesLoading = false;
      });

    builder.addCase(getAdminChatMessages.pending, (state) => {
      state.adminChat.isLoading = true;
    });
    builder
      .addCase(getAdminChatMessages.fulfilled, (state, action) => {
        const { payload, meta } = action;
        state.adminChat.isLoading = false;

        // Drop stale responses when the user has switched chats mid-flight.
        // Only enforced once a chat is selected — initial-load fetches dispatch
        // before getAdminSupportMessages resolves selectedChat.
        if (
          meta.arg.supportChatId &&
          state.adminChat.selectedChat?.support_chat_id &&
          meta.arg.supportChatId !==
            state.adminChat.selectedChat.support_chat_id
        ) {
          return;
        }

        const { selectedChatItems } = state.adminChat;

        // No cursor = first page = replace. Cursor = pagination = append.
        // Replacing matters on remount: redux preserves selectedChatItems
        // across AdminChat unmount, so the mount-time fetch would otherwise
        // concat the same page onto itself.
        if (!meta.arg.cursor) {
          state.adminChat.selectedChatItems = payload;
        } else if (selectedChatItems) {
          selectedChatItems.data = selectedChatItems.data?.concat(
            payload.data ?? [],
          );
          selectedChatItems.page_size = payload.page_size;
          selectedChatItems.next_cursor = payload.next_cursor;
          selectedChatItems.total_chat_count = payload.total_chat_count;
        }
      })
      .addCase(getAdminChatMessages.rejected, (state) => {
        state.adminChat.isLoading = false;
      });

    builder.addCase(resolveSupportChat.pending, (state) => {
      state.adminChat.isResolving = true;
    });
    builder.addCase(resolveSupportChat.fulfilled, (state, action) => {
      const { payload } = action;

      if (state.adminChat.selectedChat) {
        state.adminChat.selectedChat = {
          ...state.adminChat.selectedChat,
          is_resolved: payload.success,
        };
      }
      if (state.adminChat.supportChats?.data) {
        state.adminChat.supportChats.data =
          state.adminChat.supportChats.data.map((chat) => {
            if (
              chat.support_chat_id ===
              state.adminChat.selectedChat?.support_chat_id
            ) {
              return {
                ...chat,
                is_resolved: payload.success,
              };
            }
            return chat;
          });
      }

      state.adminChat.isResolving = false;
    });
    builder.addCase(resolveSupportChat.rejected, (state) => {
      state.adminChat.isResolving = false;
    });

    builder.addCase(sendAdminChatMessage.fulfilled, (state, action) => {
      if (action.payload) {
        const inserted = unshiftMessageIfNew(
          state.adminChat.selectedChatItems?.data,
          action.payload,
        );
        if (state.adminChat.selectedChatItems && inserted) {
          state.adminChat.selectedChatItems.total_chat_count =
            (state.adminChat.selectedChatItems.total_chat_count ?? 0) + 1;
        }
        const { supportChats, selectedChat } = state.adminChat;
        const selectedChatId = action.payload.support_chat_id;

        if (supportChats?.data) {
          supportChats.data = supportChats.data.filter(
            (item) => item.support_chat_id !== selectedChatId,
          );

          const updatedChat: SupportChat = {
            ...state.adminChat.selectedChat!,
            created_at: action.payload.created_at,
            latest_message: action.payload.message,
          };

          supportChats.data.unshift({ ...updatedChat, is_resolved: false });
        }

        if (selectedChat?.is_resolved) {
          selectedChat.is_resolved = false;
        }
      }
    });
    builder.addCase(getOrgJobsUnreadMessageCount.fulfilled, (state, action) => {
      state.employerOrgJobsUnreadMessage = action.payload;
    });
    builder.addCase(getAdminChatStats.fulfilled, (state, action) => {
      state.adminChat.chatStats = action.payload;
    });
    builder.addCase(fetchEmployerPostedJobs.fulfilled, (state, action) => {
      state.employerPostedJobs = action.payload;
    });
    builder.addCase(updateSupportOrTeamChat.fulfilled, (state, action) => {
      // if chat is admin support
      if (
        action.payload.support_chat_id &&
        action.payload.to_user_id &&
        state.adminChat.selectedChatItems?.data
      ) {
        state.adminChat.selectedChatItems.data =
          state.adminChat.selectedChatItems.data.map((chat) => {
            if (chat.id === action.payload.id) {
              return {
                ...action.payload,
              };
            }
            return chat;
          });
      }

      // if chat is related to team (group or direact message)
      else if (
        (state.floatingChatBox.selectedTeamChat?.id ===
          action.payload.to_chat_group_id ||
          state.floatingChatBox.selectedTeamChat?.to_user_id ===
            action.payload.to_user_id) &&
        state.floatingChatBox.selectedTeamChatItems?.data
      ) {
        state.floatingChatBox.selectedTeamChatItems.data =
          state.floatingChatBox.selectedTeamChatItems.data?.map((chat) => {
            if (chat.id === action.payload.id) {
              return { ...action.payload };
            }
            return chat;
          });
      }

      // if chat is related to support
      else if (
        state.floatingChatBox.selectedChat?.support_chat_id ===
          action.payload.support_chat_id &&
        state.floatingChatBox.selectedChatItems?.data
      ) {
        state.floatingChatBox.selectedChatItems.data =
          state.floatingChatBox.selectedChatItems.data.map((chat) => {
            if (chat.id === action.payload.id) {
              return { ...action.payload };
            }
            return chat;
          });
      }
    });
    builder.addCase(deleteSupportTeamChat.fulfilled, (state, action) => {
      if (
        action.payload.type === 'adminSupport' &&
        state.adminChat.selectedChatItems?.data
      ) {
        state.adminChat.selectedChatItems.data =
          state.adminChat.selectedChatItems.data.filter(
            (chat) => chat.id !== action.payload.chatId,
          );
        state.adminChat.selectedChatItems.total_chat_count =
          (state.adminChat.selectedChatItems.total_chat_count ?? 1) - 1;
      } else if (
        action.payload.type === 'support' &&
        state.floatingChatBox.selectedChatItems?.data
      ) {
        state.floatingChatBox.selectedChatItems.data =
          state.floatingChatBox.selectedChatItems.data.filter(
            (chat) => chat.id !== action.payload.chatId,
          );
        state.floatingChatBox.selectedChatItems.total_chat_count =
          (state.floatingChatBox.selectedChatItems.total_chat_count ?? 1) - 1;
      } else if (
        action.payload.type === 'team' &&
        state.floatingChatBox.selectedTeamChatItems?.data
      ) {
        state.floatingChatBox.selectedTeamChatItems.data =
          state.floatingChatBox.selectedTeamChatItems.data.filter(
            (chat) => chat.id !== action.payload.chatId,
          );
        state.floatingChatBox.selectedTeamChatItems.total_chat_count =
          (state.floatingChatBox.selectedTeamChatItems.total_chat_count ?? 1) -
          1;
      }
    });
    builder.addCase(
      fetchTeamSupportUnreadMessageCount.fulfilled,
      (state, action) => {
        state.floatingChatBox.unreadCounts = action.payload;
      },
    );
    builder.addCase(getApplicantStatusInfo.fulfilled, (state, action) => {
      state.applicantJobsUnreadMessageCount =
        action.payload.unread_chat_count ?? 0;
    });
    builder.addCase(markJobChatSeen.fulfilled, (state) => {
      if (state.chats?.all_chats) {
        state.chats.all_chats = state.chats.all_chats.map((chat) => ({
          ...chat,
          is_chat_seen: true,
        }));
      }
      // Reinforce the org-inbox clear once the server has actually marked the
      // thread seen: the optimistic clear in setEmployerSelectedApplicant can
      // be undone by a concurrent inbox refetch that still sees unread server
      // -side (the mark-seen request lands slightly later). Keyed off the open
      // conversation, since this action carries no applicant identity itself.
      const selected = state.employerSelectedApplicant;
      if (selected?.user?.id && state.orgApplicantChats?.applicants) {
        state.orgApplicantChats.applicants =
          state.orgApplicantChats.applicants.map((applicant) =>
            applicant.id === selected.user?.id &&
            applicant.job_alias === selected.job_alias
              ? { ...applicant, unread_count: 0 }
              : applicant,
          );
      }
    });
    builder.addCase(initiateSupportChat.fulfilled, () => {
      // No-op: a fresh support thread is a client-only draft until the employer
      // sends the first message. The list row is added in sendChatMessage.fulfilled
      // once the backend has created the real support_chat row.
    });
    builder.addCase(markSupportTeamChatSeen.fulfilled, (state, action) => {
      if (
        action.payload.type === 'adminSupport' &&
        state.adminChat.selectedChatItems?.data
      ) {
        state.adminChat.selectedChatItems.data =
          state.adminChat.selectedChatItems.data.map((chat) => ({
            ...chat,
            is_chat_seen: true,
          }));
      } else if (
        action.payload.type === 'support' &&
        state.floatingChatBox.selectedChatItems?.data
      ) {
        state.floatingChatBox.selectedChatItems.data =
          state.floatingChatBox.selectedChatItems.data.map((chat) => ({
            ...chat,
            is_chat_seen: true,
          }));
      } else if (
        action.payload.type === 'team' &&
        state.floatingChatBox.selectedTeamChatItems?.data
      ) {
        state.floatingChatBox.selectedTeamChatItems.data =
          state.floatingChatBox.selectedTeamChatItems.data.map((chat) => ({
            ...chat,
            is_chat_seen: true,
          }));
      }
    });
  },
});

export const {
  setFilters,
  setApplicantSelectedJob,
  setEmployerSelectedApplicant,
  setEmployerSelectedApplicantHiringStatus,
  setEmployerSelectedJobIdOrgId,
  addChatReactionSocket,
  deleteChatReactionSocket,
  chatSeenSocket,
  updateChatReactionSocket,
  setAdminSupportChatFilter,
  decrementFloatingChatUnreadCount,
  toggleFloatingChatBox,
  updateSearchQuery,
  toggleChatView,
  updateChatScreen,
  updateEmployerSelectedChat,
  updateEmployerSupportChats,
  employerNewGroupCreateSocket,
  employerSupportResolvedSocket,
  employerSupportTeamChatSeenSocket,
  updateGroupChats,
  updateSelectedGroupMembers,
  updateSelectedGroupChat,
  updateGroupData,
  updateStateForInitiateDirectChat,
  resetEmployerChat,
  updateAdminSelectedChat,
  updateAdminSelectedChatItems,
  updateAdminSupportChats,
  updateAdminChatStats,
  adminChatSeenSocket,
  adminNewMessageRecievedSocket,
  decrementOrgJobsUnreadMessage,
  employerNewSupportTeamMessageRecievedSocket,
  newJobChatMessageRecievedSocket,
  resetApplicantChat,
  resetFloatingChatOnOrgChange,
  updateIsChatBubbleVisible,
  openChatDrawer,
  closeChatDrawer,
} = chatSlice.actions;

export default chatSlice.reducer;
